'use client';

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DollarSign,
  ShoppingCart,
  ArrowUpCircle,
  Users,
  Repeat,
  XCircle,
  RefreshCw,
  Search,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateRangePicker, formatParisDate } from '@/components/ui/date-range-picker';
import {
  ResponsiveContainer,
  LineChart,
  Line as ReLine,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { DateRange } from 'react-day-picker';
import { trpc } from '@/lib/api/trpc';
import { formatMoney } from '@/lib/money';
import { useUrlDateRange } from '@/hooks/use-url-date-range';

function MetricSummary({
  title,
  value,
  subtitle,
  icon: Icon,
  isLoading,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-4">
      <div className="flex items-center space-x-3">
        <div className="bg-primary/10 rounded-lg p-2">
          <Icon className="text-primary h-5 w-5" />
        </div>
        <div>
          <p className="text-muted-foreground text-sm font-medium">{title}</p>
          {isLoading ? (
            <Skeleton className="mt-1 h-7 w-32" />
          ) : (
            <>
              <p className="text-lg font-semibold">{value}</p>
              {subtitle && <p className="text-muted-foreground text-xs">{subtitle}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SimpleBarChart({
  data,
  currency,
  isLoading,
  emptyMessage,
}: {
  data: { date: string; value: number }[];
  currency: string;
  isLoading: boolean;
  emptyMessage?: string;
}) {
  const { t } = useTranslation('analytics');
  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (data.length === 0) {
    return (
      <div className="text-muted-foreground flex h-48 items-center justify-center text-sm">
        {emptyMessage ?? t('adminProcessing.noData')}
      </div>
    );
  }

  const total = data.reduce((s, d) => s + d.value, 0);
  const chartData = data.map((d) => ({
    date: d.date,
    label: format(new Date(d.date), 'M/d'),
    value: d.value,
  }));

  return (
    <div>
      <ResponsiveContainer width="100%" height={192}>
        <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <Tooltip
            formatter={(value: number) => [formatMoney(value, currency), ''] as [string, string]}
            labelFormatter={(_label: string, payload) => {
              const raw = payload?.[0]?.payload?.date as string | undefined;
              return raw ? format(new Date(raw), 'MMM d, yyyy') : '';
            }}
            contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
            separator=""
          />
          <Bar dataKey="value" fill="#3b82f6" fillOpacity={0.85} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-1 text-right text-xs font-medium text-muted-foreground">
        {t('adminProcessing.chartTotal', { amount: formatMoney(total, currency) })}
      </div>
    </div>
  );
}

type MapEntry = Record<string, { revenue?: number; amount?: number; saleCount?: number; transactionCount?: number; upsellCount?: number; refundCount?: number }>;

function mapToChartData(data: MapEntry | undefined, valueKey: string): { date: string; value: number }[] {
  if (!data) return [];
  return Object.entries(data)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, entry]) => ({
      date,
      value: Number((entry as any)[valueKey] ?? 0),
    }));
}

function ApprovalRateTrend({
  straightSaleData,
  rebillData,
  isLoading,
}: {
  straightSaleData: MapEntry | undefined;
  rebillData: MapEntry | undefined;
  isLoading: boolean;
}) {
  const chartData = useMemo(() => {
    const allDates = new Set<string>();
    if (straightSaleData) Object.keys(straightSaleData).forEach(d => allDates.add(d));
    if (rebillData) Object.keys(rebillData).forEach(d => allDates.add(d));

    return Array.from(allDates).sort().map(date => {
      const ss = straightSaleData?.[date] as any;
      const rb = rebillData?.[date] as any;

      const citApproved = ss?.saleCount ?? 0;
      const citTotal = citApproved + (ss?.declinedCount ?? ss?.declined ?? 0);
      const mitApproved = rb?.transactionCount ?? 0;
      const mitTotal = mitApproved + (rb?.declinedCount ?? rb?.declined ?? 0);

      const totalApproved = citApproved + mitApproved;
      const totalAll = citTotal + mitTotal;

      return {
        date,
        label: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        CIT: citTotal > 0 ? (citApproved / citTotal) * 100 : null,
        MIT: mitTotal > 0 ? (mitApproved / mitTotal) * 100 : null,
        Overall: totalAll > 0 ? (totalApproved / totalAll) * 100 : null,
      };
    });
  }, [straightSaleData, rebillData]);

  const { t } = useTranslation('analytics');

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('adminProcessing.approvalRateTrend')}</CardTitle>
        </CardHeader>
        <CardContent><Skeleton className="h-[300px] w-full" /></CardContent>
      </Card>
    );
  }

  if (chartData.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t('adminProcessing.approvalRateTrend')}</CardTitle>
        <CardDescription>{t('adminProcessing.approvalTrendDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
            <Tooltip
              formatter={(value: any, name: string) => [value != null ? `${Number(value).toFixed(1)}%` : 'N/A', name]}
              contentStyle={{ borderRadius: '8px', fontSize: '13px' }}
            />
            <Legend />
            <ReLine type="monotone" dataKey="Overall" stroke="#3b82f6" strokeWidth={2.5} dot={false} connectNulls />
            <ReLine type="monotone" dataKey="CIT" stroke="#22c55e" strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls />
            <ReLine type="monotone" dataKey="MIT" stroke="#f97316" strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

interface BreakdownRow {
  accountId: string;
  orgName: string;
  orgSlug: string | null;
  partner: string | null;
  storeId?: string | null;
  storeName?: string | null;
  approvedVolume: number;
  approvedCount: number;
  declinedCount: number;
  refundVolume: number;
  refundCount: number;
  rebillVolume: number;
  rebillCount: number;
  straightSaleVolume: number;
  straightSaleCount: number;
  netRevenue: number;
  storeCount: number;
}

function getDefaultProcessingRange(): DateRange {
  const BILLING_TZ = 'Europe/Paris'
  const parts: Record<string, string> = {}
  new Intl.DateTimeFormat('en-US', {
    timeZone: BILLING_TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour12: false,
  }).formatToParts(new Date()).forEach(p => { parts[p.type] = p.value })
  const y = Number(parts.year), mo = Number(parts.month) - 1, d = Number(parts.day)
  const noonUTC = new Date(Date.UTC(y, mo, d, 12))
  const utcStr = noonUTC.toLocaleString('en-US', { timeZone: 'UTC' })
  const parisStr = noonUTC.toLocaleString('en-US', { timeZone: BILLING_TZ })
  const offsetMs = new Date(parisStr).getTime() - new Date(utcStr).getTime()
  const toDate = new Date(Date.UTC(y, mo, d) - offsetMs)
  const fromDate = new Date(Date.UTC(y, mo, d - 29) - offsetMs)
  return { from: fromDate, to: toDate }
}

export default function AdminProcessingPage() {
  const { t } = useTranslation('analytics');
  const [dateRange, setDateRange] = useUrlDateRange(getDefaultProcessingRange);
  const [currency] = useState('USD');

  const dates = useMemo(() => ({
    startDate: dateRange.from ?? new Date(),
    endDate: dateRange.to ?? new Date(),
  }), [dateRange]);

  const filters = {
    startDate: dates.startDate,
    endDate: dates.endDate,
    storeIds: [] as string[],
    currency,
  };

  const queryOpts = { staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false };

  const [breakdownSearch, setBreakdownSearch] = useState('');
  const [breakdownView, setBreakdownView] = useState<'account' | 'store'>('account');

  const straightSale = trpc.admin.globalStraightSale.useQuery(filters, queryOpts);
  const rebillRevenue = trpc.admin.globalRebillRevenue.useQuery(filters, queryOpts);
  const upsellRevenue = trpc.admin.globalUpsellRevenue.useQuery(filters, queryOpts);
  const refunds = trpc.admin.globalRefunds.useQuery(filters, queryOpts);
  const approvalRates = trpc.admin.globalApprovalRates.useQuery(filters, queryOpts);
  const activeSubs = trpc.admin.globalActiveSubscriptions.useQuery(filters, queryOpts);
  const subsGained = trpc.admin.globalSubscriptionsGained.useQuery(filters, queryOpts);
  const subsCancelled = trpc.admin.globalSubscriptionsCancelled.useQuery(filters, queryOpts);
  const revenueBreakdown = trpc.admin.globalRevenueBreakdown.useQuery(filters, queryOpts);
  const backfillOrgNames = trpc.admin.backfillOrgNames.useMutation({
    onSuccess: () => revenueBreakdown.refetch(),
  });

  const straightSaleData = mapToChartData(straightSale.data as MapEntry, 'revenue');
  const rebillData = mapToChartData(rebillRevenue.data as MapEntry, 'amount');
  const upsellData = mapToChartData(upsellRevenue.data as MapEntry, 'revenue');
  const refundData = mapToChartData(refunds.data as MapEntry, 'amount');

  const totalStraightSale = straightSaleData.reduce((s, d) => s + d.value, 0);
  const totalRebill = rebillData.reduce((s, d) => s + d.value, 0);
  const totalUpsell = upsellData.reduce((s, d) => s + d.value, 0);
  const totalRefunds = refundData.reduce((s, d) => s + d.value, 0);
  const totalRevenue = totalStraightSale + totalRebill + totalUpsell - totalRefunds;

  const straightSaleVolume = straightSale.data
    ? Object.values(straightSale.data as MapEntry).reduce((s, d) => s + (d.saleCount ?? 0), 0)
    : 0;
  const rebillVolume = rebillRevenue.data
    ? Object.values(rebillRevenue.data as MapEntry).reduce((s, d) => s + (d.transactionCount ?? 0), 0)
    : 0;
  const upsellVolume = upsellRevenue.data
    ? Object.values(upsellRevenue.data as MapEntry).reduce((s, d) => s + (d.upsellCount ?? 0), 0)
    : 0;
  const refundVolume = refunds.data
    ? Object.values(refunds.data as MapEntry).reduce((s, d) => s + (d.refundCount ?? 0), 0)
    : 0;

  const activeSubsEntries = activeSubs.data ? Object.entries(activeSubs.data as Record<string, number>).sort(([a], [b]) => a.localeCompare(b)) : [];
  const latestActiveSubs = activeSubsEntries.length > 0 ? activeSubsEntries[activeSubsEntries.length - 1][1] : 0;

  const totalSubsGained = subsGained.data ? Object.values(subsGained.data as Record<string, number>).reduce((s, v) => s + v, 0) : 0;
  const totalSubsCancelled = subsCancelled.data ? Object.values(subsCancelled.data as Record<string, number>).reduce((s, v) => s + v, 0) : 0;

  const breakdownRows = useMemo((): BreakdownRow[] => {
    const raw = revenueBreakdown.data ?? [];
    const search = breakdownSearch.toLowerCase();

    if (breakdownView === 'account') {
      const map = new Map<string, BreakdownRow>();
      for (const r of raw) {
        const existing = map.get(r.accountId);
        if (existing) {
          existing.approvedVolume += r.approvedVolume;
          existing.approvedCount += r.approvedCount;
          existing.declinedCount += r.declinedCount;
          existing.refundVolume += r.refundVolume;
          existing.refundCount += r.refundCount;
          existing.rebillVolume += r.rebillVolume;
          existing.rebillCount += r.rebillCount;
          existing.straightSaleVolume += r.straightSaleVolume;
          existing.straightSaleCount += r.straightSaleCount;
          existing.netRevenue += r.netRevenue;
          existing.storeCount++;
        } else {
          map.set(r.accountId, { ...r, storeCount: 1 });
        }
      }
      let rows = [...map.values()];
      if (search) {
        rows = rows.filter(r =>
          r.orgName.toLowerCase().includes(search) ||
          (r.orgSlug ?? '').toLowerCase().includes(search) ||
          r.accountId.toLowerCase().includes(search) ||
          (r.partner ?? '').toLowerCase().includes(search)
        );
      }
      return rows.sort((a, b) => b.approvedVolume - a.approvedVolume);
    }

    let rows: BreakdownRow[] = raw.map(r => ({ ...r, storeCount: 0 }));
    if (search) {
      rows = rows.filter(r =>
        (r.storeName ?? '').toLowerCase().includes(search) ||
        r.orgName.toLowerCase().includes(search) ||
        (r.orgSlug ?? '').toLowerCase().includes(search) ||
        r.accountId.toLowerCase().includes(search)
      );
    }
    return rows.sort((a, b) => b.approvedVolume - a.approvedVolume);
  }, [revenueBreakdown.data, breakdownSearch, breakdownView]);

  const breakdownTotals = useMemo(() => {
    return breakdownRows.reduce((acc, r) => ({
      approvedVolume: acc.approvedVolume + r.approvedVolume,
      approvedCount: acc.approvedCount + r.approvedCount,
      declinedCount: acc.declinedCount + r.declinedCount,
      refundVolume: acc.refundVolume + r.refundVolume,
      refundCount: acc.refundCount + r.refundCount,
      rebillVolume: acc.rebillVolume + r.rebillVolume,
      rebillCount: acc.rebillCount + r.rebillCount,
      straightSaleVolume: acc.straightSaleVolume + r.straightSaleVolume,
      straightSaleCount: acc.straightSaleCount + r.straightSaleCount,
      netRevenue: acc.netRevenue + r.netRevenue,
    }), {
      approvedVolume: 0, approvedCount: 0, declinedCount: 0,
      refundVolume: 0, refundCount: 0, rebillVolume: 0, rebillCount: 0,
      straightSaleVolume: 0, straightSaleCount: 0, netRevenue: 0,
    });
  }, [breakdownRows]);

  const hasUnresolvedOrgNames = useMemo(() => {
    return (revenueBreakdown.data ?? []).some(r => r.orgName.startsWith('acc_'));
  }, [revenueBreakdown.data]);

  const approvalData = approvalRates.data as any;

  const citAttempts = approvalData?.cit?.orderLevel?.withOrchestration?.attempts ?? 0;
  const citSuccesses = approvalData?.cit?.orderLevel?.withOrchestration?.successes ?? 0;
  const citApprovalRate = citAttempts > 0 ? (citSuccesses / citAttempts) * 100 : null;

  const mitCycles = approvalData?.mit ?? {};
  const mitTotals = Object.values(mitCycles).reduce<{ attempts: number; successes: number }>(
    (acc, cycle: any) => ({
      attempts: acc.attempts + (cycle?.withRetries?.attempts ?? 0),
      successes: acc.successes + (cycle?.withRetries?.successes ?? 0),
    }),
    { attempts: 0, successes: 0 }
  );
  const mitApprovalRate = mitTotals.attempts > 0 ? (mitTotals.successes / mitTotals.attempts) * 100 : null;

  const overallAttempts = citAttempts + mitTotals.attempts;
  const overallSuccesses = citSuccesses + mitTotals.successes;
  const overallApprovalRate = overallAttempts > 0 ? (overallSuccesses / overallAttempts) * 100 : null;

  const billingCycleData = useMemo(() => {
    if (!approvalData?.mit) return [];
    return Object.entries(approvalData.mit as Record<string, any>)
      .map(([chainIndex, cycle]) => ({
        chainIndex: Number(chainIndex),
        totalPayments: cycle.withRetries?.attempts ?? 0,
        approved: cycle.withRetries?.successes ?? 0,
        approvalRate: cycle.withRetries?.attempts > 0
          ? (cycle.withRetries.successes / cycle.withRetries.attempts) * 100
          : null,
      }))
      .sort((a, b) => a.chainIndex - b.chainIndex);
  }, [approvalData]);

  const anyLoading = straightSale.isLoading || rebillRevenue.isLoading || upsellRevenue.isLoading || refunds.isLoading;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <DateRangePicker value={dateRange} onChange={setDateRange} />
        <span className="text-muted-foreground text-xs">
          {formatParisDate(dates.startDate, { month: 'short', day: 'numeric', year: 'numeric' })}
          {' — '}
          {formatParisDate(dates.endDate, { month: 'short', day: 'numeric', year: 'numeric' })}
          {' '}
          {t('adminProcessing.parisTime')}
        </span>
      </div>

      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <MetricSummary
          title={t('adminProcessing.netRevenue')}
          value={formatMoney(totalRevenue, currency)}
          subtitle={`${(straightSaleVolume + rebillVolume + upsellVolume).toLocaleString()} ${t('adminProcessing.txns')}`}
          icon={DollarSign}
          isLoading={anyLoading}
        />
        <MetricSummary
          title={t('adminProcessing.straightSale')}
          value={formatMoney(totalStraightSale, currency)}
          subtitle={`${straightSaleVolume.toLocaleString()} ${t('adminProcessing.sales')}`}
          icon={ShoppingCart}
          isLoading={straightSale.isLoading}
        />
        <MetricSummary
          title={t('adminProcessing.rebillRevenue')}
          value={formatMoney(totalRebill, currency)}
          subtitle={`${rebillVolume.toLocaleString()} ${t('adminProcessing.rebills')}`}
          icon={Repeat}
          isLoading={rebillRevenue.isLoading}
        />
        <MetricSummary
          title={t('adminProcessing.upsellRevenue')}
          value={formatMoney(totalUpsell, currency)}
          subtitle={`${upsellVolume.toLocaleString()} ${t('adminProcessing.upsells')}`}
          icon={ArrowUpCircle}
          isLoading={upsellRevenue.isLoading}
        />
        <MetricSummary
          title={t('adminProcessing.refunds')}
          value={formatMoney(totalRefunds, currency)}
          subtitle={`${refundVolume.toLocaleString()} ${t('adminProcessing.refundsLabel')}`}
          icon={XCircle}
          isLoading={refunds.isLoading}
        />
        <MetricSummary
          title={t('adminProcessing.activeSubs')}
          value={latestActiveSubs.toLocaleString()}
          subtitle={t('adminProcessing.subsGainedLost', {
            gained: totalSubsGained.toLocaleString(),
            cancelled: totalSubsCancelled.toLocaleString(),
          })}
          icon={Users}
          isLoading={activeSubs.isLoading}
        />
      </div>

      {/* Approval Rates */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('adminProcessing.overallApprovalRate')}</CardTitle>
          </CardHeader>
          <CardContent>
            {approvalRates.isLoading ? (
              <Skeleton className="h-10 w-20" />
            ) : (
              <>
                <div className="text-3xl font-bold">
                  {overallApprovalRate != null ? `${overallApprovalRate.toFixed(1)}%` : 'N/A'}
                </div>
                {overallAttempts > 0 && (
                  <p className="text-muted-foreground text-xs mt-1">{overallSuccesses.toLocaleString()} / {overallAttempts.toLocaleString()} {t('adminProcessing.payments')}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('adminProcessing.citApprovalRate')}</CardTitle>
            <CardDescription>{t('adminProcessing.citDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {approvalRates.isLoading ? (
              <Skeleton className="h-10 w-20" />
            ) : (
              <>
                <div className="text-3xl font-bold">
                  {citApprovalRate != null ? `${citApprovalRate.toFixed(1)}%` : 'N/A'}
                </div>
                {citAttempts > 0 && (
                  <p className="text-muted-foreground text-xs mt-1">{citSuccesses.toLocaleString()} / {citAttempts.toLocaleString()} {t('adminProcessing.payments')}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('adminProcessing.mitApprovalRate')}</CardTitle>
            <CardDescription>{t('adminProcessing.mitDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {approvalRates.isLoading ? (
              <Skeleton className="h-10 w-20" />
            ) : (
              <>
                <div className="text-3xl font-bold">
                  {mitApprovalRate != null ? `${mitApprovalRate.toFixed(1)}%` : 'N/A'}
                </div>
                {mitTotals.attempts > 0 && (
                  <p className="text-muted-foreground text-xs mt-1">{mitTotals.successes.toLocaleString()} / {mitTotals.attempts.toLocaleString()} {t('adminProcessing.payments')}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Approval Rate Trend */}
      <ApprovalRateTrend
        straightSaleData={straightSale.data as MapEntry}
        rebillData={rebillRevenue.data as MapEntry}
        isLoading={straightSale.isLoading || rebillRevenue.isLoading}
      />

      {/* Revenue Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('adminProcessing.straightSaleRevenue')}</CardTitle>
            <CardDescription>{t('adminProcessing.straightSaleDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <SimpleBarChart
              data={straightSaleData}
              currency={currency}
              isLoading={straightSale.isLoading}
              emptyMessage={t('adminProcessing.noStraightSaleData')}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('adminProcessing.rebillRevenue')}</CardTitle>
            <CardDescription>{t('adminProcessing.rebillDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <SimpleBarChart
              data={rebillData}
              currency={currency}
              isLoading={rebillRevenue.isLoading}
              emptyMessage={t('adminProcessing.noRebillData')}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('adminProcessing.upsellRevenue')}</CardTitle>
            <CardDescription>{t('adminProcessing.upsellDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <SimpleBarChart
              data={upsellData}
              currency={currency}
              isLoading={upsellRevenue.isLoading}
              emptyMessage={t('adminProcessing.noUpsellData')}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('adminProcessing.refunds')}</CardTitle>
            <CardDescription>{t('adminProcessing.refundsDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <SimpleBarChart
              data={refundData}
              currency={currency}
              isLoading={refunds.isLoading}
              emptyMessage={t('adminProcessing.noRefundData')}
            />
          </CardContent>
        </Card>
      </div>

      {/* Approval Rates by Billing Cycle */}
      {billingCycleData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('adminProcessing.mitByBillingCycle')}</CardTitle>
            <CardDescription>{t('adminProcessing.mitByCycleDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('adminProcessing.cycle')}</TableHead>
                    <TableHead className="text-right">{t('adminProcessing.totalPayments')}</TableHead>
                    <TableHead className="text-right">{t('adminProcessing.approvalRate')}</TableHead>
                    <TableHead className="text-right">{t('adminProcessing.approvalRate')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billingCycleData.slice(0, 15).map((cycle) => (
                    <TableRow key={cycle.chainIndex}>
                      <TableCell>{t('adminProcessing.cycleNumber', { number: cycle.chainIndex })}</TableCell>
                      <TableCell className="text-right">{cycle.totalPayments.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{cycle.approved.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {cycle.approvalRate != null ? `${cycle.approvalRate.toFixed(1)}%` : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue Breakdown per Account / Store */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg">{t('adminProcessing.revenueBreakdown')}</CardTitle>
              <CardDescription>{t('adminProcessing.revenueBreakdownDescription')}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {hasUnresolvedOrgNames && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => backfillOrgNames.mutate()}
                  disabled={backfillOrgNames.isPending}
                >
                  <RefreshCw className={`mr-2 h-3 w-3 ${backfillOrgNames.isPending ? 'animate-spin' : ''}`} />
                  {t('adminProcessing.syncOrgNames')}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Select value={breakdownView} onValueChange={(v) => setBreakdownView(v as 'account' | 'store')}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="account">{t('adminProcessing.byAccount')}</SelectItem>
                <SelectItem value="store">{t('adminProcessing.byStore')}</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative max-w-sm flex-1">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder={t('adminProcessing.searchPlaceholder')}
                value={breakdownSearch}
                onChange={(e) => setBreakdownSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <span className="text-muted-foreground text-xs">
              {breakdownRows.length} {breakdownView === 'account' ? t('adminProcessing.accounts') : t('adminProcessing.stores')}
            </span>
          </div>

          {revenueBreakdown.isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : breakdownRows.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {breakdownSearch ? t('adminProcessing.noMatchingResults') : t('adminProcessing.noDataAvailable')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>{breakdownView === 'account' ? t('adminRevenue.account') : t('adminProcessing.store')}</TableHead>
                    {breakdownView === 'store' && <TableHead>{t('adminRevenue.account')}</TableHead>}
                    <TableHead>{t('adminRevenue.partner')}</TableHead>
                    <TableHead className="text-right">{t('adminProcessing.approvedVol')}</TableHead>
                    <TableHead className="text-right">{t('adminProcessing.straightSaleCol')}</TableHead>
                    <TableHead className="text-right">{t('adminProcessing.rebill')}</TableHead>
                    <TableHead className="text-right">{t('adminProcessing.refunds')}</TableHead>
                    <TableHead className="text-right">{t('adminProcessing.netRevenueCol')}</TableHead>
                    <TableHead className="text-right">{t('adminRevenue.approved')}</TableHead>
                    <TableHead className="text-right">{t('adminRevenue.declined')}</TableHead>
                    <TableHead className="text-right">{t('adminRevenue.rate')}</TableHead>
                    {breakdownView === 'account' && <TableHead className="text-right">{t('adminProcessing.storesCount')}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakdownRows.map((row, idx) => {
                    const totalTxns = row.approvedCount + row.declinedCount;
                    const rate = totalTxns > 0 ? (row.approvedCount / totalTxns) * 100 : 0;
                    return (
                      <TableRow key={breakdownView === 'account' ? row.accountId : `${row.accountId}-${row.storeId}`}>
                        <TableCell className="text-muted-foreground font-mono text-xs">{idx + 1}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">
                              {breakdownView === 'account' ? row.orgName : (row.storeName ?? t('adminProcessing.unknownStore'))}
                            </span>
                            <span className="text-muted-foreground font-mono text-xs">
                              {breakdownView === 'account'
                                ? (row.orgSlug ?? row.accountId.slice(0, 18))
                                : row.storeId?.slice(0, 18)}
                            </span>
                          </div>
                        </TableCell>
                        {breakdownView === 'store' && (
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="text-xs">{row.orgName}</span>
                              {row.orgSlug && (
                                <span className="text-muted-foreground font-mono text-xs">{row.orgSlug}</span>
                              )}
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge variant={row.partner && row.partner !== 'tagada' ? 'outline' : 'default'} className="text-xs">
                            {row.partner ?? 'tagada'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatMoney(row.approvedVolume, currency)}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {formatMoney(row.straightSaleVolume, currency)}
                          <span className="text-muted-foreground ml-1 text-xs">({row.straightSaleCount})</span>
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {formatMoney(row.rebillVolume, currency)}
                          <span className="text-muted-foreground ml-1 text-xs">({row.rebillCount})</span>
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {formatMoney(row.refundVolume, currency)}
                          <span className="text-muted-foreground ml-1 text-xs">({row.refundCount})</span>
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          {formatMoney(row.netRevenue, currency)}
                        </TableCell>
                        <TableCell className="text-right">{row.approvedCount.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{row.declinedCount.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {rate.toFixed(1)}%
                        </TableCell>
                        {breakdownView === 'account' && (
                          <TableCell className="text-right">{row.storeCount}</TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
                {breakdownRows.length > 0 && (
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={breakdownView === 'store' ? 4 : 3} className="font-semibold">
                        {t('adminProcessing.totalCount', { count: breakdownRows.length })}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {formatMoney(breakdownTotals.approvedVolume, currency)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-sm">
                        {formatMoney(breakdownTotals.straightSaleVolume, currency)}
                        <span className="text-muted-foreground ml-1 text-xs">({breakdownTotals.straightSaleCount})</span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-sm">
                        {formatMoney(breakdownTotals.rebillVolume, currency)}
                        <span className="text-muted-foreground ml-1 text-xs">({breakdownTotals.rebillCount})</span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-sm">
                        {formatMoney(breakdownTotals.refundVolume, currency)}
                        <span className="text-muted-foreground ml-1 text-xs">({breakdownTotals.refundCount})</span>
                      </TableCell>
                      <TableCell className="text-right font-bold text-sm">
                        {formatMoney(breakdownTotals.netRevenue, currency)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{breakdownTotals.approvedCount.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold">{breakdownTotals.declinedCount.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-bold">
                        {(breakdownTotals.approvedCount + breakdownTotals.declinedCount) > 0
                          ? `${((breakdownTotals.approvedCount / (breakdownTotals.approvedCount + breakdownTotals.declinedCount)) * 100).toFixed(1)}%`
                          : 'N/A'}
                      </TableCell>
                      {breakdownView === 'account' && <TableCell />}
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
